/**
 * Where a tenant's audience and conversion setup actually lives in the admin app.
 *
 * The admin app's own Mapping page picks between three destinations per screen, and this
 * mirrors it. Two flags IN THE ADMIN APP decide, not anything here:
 *
 * | state                                 | audience                           | conversion                       |
 * |---------------------------------------|------------------------------------|----------------------------------|
 * | `admin-audiences` / `admin-conversions` | `/audiences`                       | `/conversions`                   |
 * | `new-admin-migration`                 | `/mapping/audiences/overview`      | `/mapping/conversion/overview`   |
 * | neither                               | LEGACY admin `/mapping/audiences`  | `/mapping/conversion/overview`   |
 *
 * The newer routes do not 404 when their flag is off. They redirect to `/org/$orgId`, so
 * a link to one lands the person on the org home with no explanation, which is what
 * Studio did for every tenant without the flags. The migrated audience page does the
 * same for a tenant that has not migrated, so the safe answer for audiences is the
 * legacy admin, where the page has always been.
 *
 * Conversions need no third state: `/mapping/conversion` redirects to the legacy admin
 * itself while the migration flag is off, so the one link works in both.
 *
 * Unresolved, failed and flag-off all give the legacy audience page and the redirecting
 * conversion page: they work for every tenant, which makes the safe answer also the one
 * to show before any answer arrives.
 */

import { buildAdminUrl, buildLegacyAdminUrl } from './adminUrl';

export interface AdminPagesEnabled {
  audiences: boolean;
  conversions: boolean;
  migration: boolean;
}

/**
 * Before the answer arrives, and after one that failed. Not a guess at the
 * flags: it is the pair of destinations that need no flag, so a person who
 * clicks during the round trip gets a working page rather than the org home.
 */
export const ADMIN_PAGES_UNRESOLVED: AdminPagesEnabled = {
  audiences: false,
  conversions: false,
  migration: false,
};

export interface AdminSetupUrls {
  audience: string;
  conversion: string;
}

export function adminSetupUrls(
  tenant: number | string,
  enabled: AdminPagesEnabled
): AdminSetupUrls {
  const audience = enabled.audiences
    ? buildAdminUrl(tenant, '/audiences')
    : enabled.migration
    ? buildAdminUrl(tenant, '/mapping/audiences/overview')
    : buildLegacyAdminUrl(tenant, '/mapping/audiences');
  const conversion = buildAdminUrl(
    tenant,
    enabled.conversions ? '/conversions' : '/mapping/conversion/overview'
  );
  return { audience, conversion };
}

// Per tenant for the life of the tab, the same reason the insights flags are
// cached: two surfaces ask (the model's metadata card and the sidebar's Setup
// section) and the answer decides which href they render.
const pending = new Map<string, Promise<AdminPagesEnabled>>();

export function fetchAdminPagesEnabled(
  tenant: string
): Promise<AdminPagesEnabled> {
  const cached = pending.get(tenant);
  if (cached) return cached;
  const answer = (async () => {
    try {
      const r = await fetch(
        `/api/tenant/${encodeURIComponent(tenant)}/admin_pages_enabled`,
        { credentials: 'same-origin' }
      );
      if (!r.ok) return ADMIN_PAGES_UNRESOLVED;
      const doc = (await r.json()) as Partial<AdminPagesEnabled>;
      return {
        audiences: !!doc.audiences,
        conversions: !!doc.conversions,
        migration: !!doc.migration,
      };
    } catch {
      return ADMIN_PAGES_UNRESOLVED;
    }
  })();
  pending.set(tenant, answer);
  return answer;
}
